/**
 * Trusted Types utility for Merlin Assistant.
 *
 * Sites like Gmail enforce a Trusted Types CSP (`require-trusted-types-for 'script'`),
 * which blocks raw string assignments to:
 *   - Element.innerHTML  (needs TrustedHTML)
 *   - Element.insertAdjacentHTML()  (needs TrustedHTML)
 *   - HTMLScriptElement.textContent  (needs TrustedScript)
 *
 * This module provides DOM-based alternatives that bypass Trusted Types enforcement
 * by using DOMParser (which is NOT a Trusted Types sink) and document.adoptNode().
 */

/**
 * Safely set innerHTML on an element using DOMParser.
 * DOMParser.parseFromString() is not a Trusted Types sink, so it works
 * on pages that enforce `require-trusted-types-for 'script'`.
 *
 * @param {HTMLElement} element - Target element
 * @param {string} html - HTML string to set
 */
export function safeSetInnerHTML(element, html) {
    // Fast path: empty string
    if (!html) {
        element.replaceChildren();
        return;
    }

    const doc = new DOMParser().parseFromString(
        `<body>${html}</body>`,
        'text/html'
    );
    element.replaceChildren();
    const nodes = Array.from(doc.body.childNodes);
    for (const node of nodes) {
        element.appendChild(document.adoptNode(node));
    }
}

/**
 * Safely call insertAdjacentHTML using DOMParser + DOM manipulation.
 *
 * @param {HTMLElement} element - Target element
 * @param {'beforebegin'|'afterbegin'|'beforeend'|'afterend'} position
 * @param {string} html - HTML string to insert
 */
export function safeInsertAdjacentHTML(element, position, html) {
    if (!html) return;

    const doc = new DOMParser().parseFromString(
        `<body>${html}</body>`,
        'text/html'
    );
    const fragment = document.createDocumentFragment();
    for (const child of Array.from(doc.body.childNodes)) {
        fragment.appendChild(document.adoptNode(child));
    }

    switch (position) {
        case 'beforeend':
            element.appendChild(fragment);
            break;
        case 'afterbegin':
            element.prepend(fragment);
            break;
        case 'beforebegin':
            element.before(fragment);
            break;
        case 'afterend':
            element.after(fragment);
            break;
    }
}
